import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Section eyebrow — the design-system micro-label that titles a section inside a
 * modal / detail view (CHP-538, matches `/styleguide`). Uppercase, tracked,
 * muted; pairs with a `<Separator />` (or a `divide-y` container) between
 * sections rather than nested white boxes, so second-level headers read clearly
 * instead of getting lost. Centralised here so the heading treatment is a
 * single-source decision the designer can sign off and retune in one place.
 */
export function SectionHeading({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn(
        "text-2xs font-semibold uppercase tracking-wider text-muted-foreground",
        className,
      )}
      {...props}
    >
      {children}
    </h3>
  );
}

/**
 * A vertical stack of detail sections separated by a thin hairline divider
 * (CHP-538) — "a thin line between the key sections" rather than nested white
 * boxes. Renders a top border between each direct child (so conditional/null
 * sections never produce a dangling rule) with even padding around each.
 */
export function DetailSections({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "divide-y divide-border [&>*]:py-5 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
